import { useState } from 'react'
import { Link } from 'react-router-dom'
import BookingModal from '../components/BookingModal'
import FontAwesomeIcon from '../components/FontAwesomeIcon'
import FAQSection from '../components/FAQSection'

export default function BwindiImpenetrable() {
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [selectedExperience, setSelectedExperience] = useState('')

  const handleBooking = (experience) => {
    setSelectedExperience(experience)
    setIsModalOpen(true)
  }

  const faqs = [
    {
      question: "How difficult is gorilla trekking?",
      answer: "Trekking difficulty varies from moderate to challenging. Hikes can last 2-8 hours over steep, muddy terrain at altitudes up to 2,700m. Moderate fitness is required, but porters are available to assist."
    },
    {
      question: "What are the gorilla permit costs?",
      answer: "Gorilla trekking permits cost $800 for foreign non-residents, $700 for foreign residents, and 300,000 UGX for East African citizens. Gorilla Habituation Experience permits cost $1,500."
    },
    {
      question: "How far in advance should I book?",
      answer: "Book permits 3-6 months in advance, especially for peak season (June-September, December-February). Last-minute availability is possible during wet seasons but not guaranteed."
    },
    {
      question: "Which sector should I choose?",
      answer: "Buhoma: Most accommodation options, established infrastructure. Rushaga: Most gorilla families, habituation available. Nkuringo: Fewer crowds, volcano views. Ruhija: Best for birding, cooler climate."
    },
    {
      question: "What is the success rate?",
      answer: "Gorilla sighting success rate exceeds 95%. Rangers track gorilla families daily and know their approximate locations. In the rare event you don't see gorillas, permits are typically reissued for the next day."
    },
    {
      question: "Can I trek if I'm not very fit?",
      answer: "Yes, though moderate fitness helps. Rangers can assign you to closer gorilla families. Porters carry gear and assist on difficult sections. Take your time and communicate with guides about your fitness level."
    },
    {
      question: "What happens if it rains during my trek?",
      answer: "Trekking continues rain or shine. Bwindi is a rainforest with frequent rainfall. Bring waterproof gear. Rain often enhances the mystical atmosphere and doesn't deter gorillas."
    },
    {
      question: "How close can I get to the gorillas?",
      answer: "Official minimum distance is 7 meters, but gorillas may approach closer (stay still if they do). You'll spend one hour observing the family, with opportunities for photography without flash."
    }
  ]

  return (
    <div>
      {/* Hero Section */}
      <section
        className="relative h-80 md:h-96 bg-cover bg-center flex items-center"
        style={{
          backgroundImage: 'linear-gradient(rgba(0,0,0,0.5), rgba(0,0,0,0.5)), url(/images/bwindi-hero.jpg)',
        }}
      >
        <div className="container mx-auto px-4 text-center text-white">
          <h1 className="text-3xl md:text-5xl font-bold mb-3 font-heading">
            Bwindi Impenetrable National Park
          </h1>
          <p className="text-base md:text-lg max-w-2xl mx-auto">
            Uganda's Premier Gorilla Trekking Destination
          </p>
        </div>
      </section>

      {/* Overview */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4 max-w-6xl">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-2xl font-bold text-safari-green mb-4">Destination Overview</h2>
              <p className="text-lg text-gray-700 leading-relaxed mb-6">
                Located in southwestern Uganda on the edge of the Albertine Rift Valley, Bwindi Impenetrable National
                Park encompasses 331 square kilometers of ancient rainforest dating back over 25,000 years. This UNESCO
                World Heritage Site delivers life-changing encounters with our closest living relatives while supporting
                critical conservation efforts that protect nearly half the world's remaining mountain gorilla population.
              </p>
              <p className="text-lg text-gray-700 leading-relaxed mb-6">
                Home to approximately 460 mountain gorillas—nearly 50% of the global population—Bwindi combines
                thrilling adventure with meaningful conservation impact. Every visitor contributes directly to gorilla
                protection and community development through Uganda's innovative revenue-sharing model, making
                each trek both personally transformative and globally significant for wildlife conservation.
              </p>
              <div className="grid grid-cols-2 gap-4">
                <div className="bg-gray-50 p-4 rounded-lg">
                  <h3 className="font-bold text-safari-green mb-2">Size</h3>
                  <p className="text-gray-700">331 km²</p>
                </div>
                <div className="bg-gray-50 p-4 rounded-lg">
                  <h3 className="font-bold text-safari-green mb-2">Elevation</h3>
                  <p className="text-gray-700">1,160 - 2,706m</p>
                </div>
                <div className="bg-gray-50 p-4 rounded-lg">
                  <h3 className="font-bold text-safari-green mb-2">Gorilla Families</h3>
                  <p className="text-gray-700">25+ habituated groups</p>
                </div>
                <div className="bg-gray-50 p-4 rounded-lg">
                  <h3 className="font-bold text-safari-green mb-2">Bird Species</h3>
                  <p className="text-gray-700">375+</p>
                </div>
              </div>
            </div>
            <div>
              <img
                src="/images/bwindi-hero.jpg"
                alt="Bwindi Impenetrable National Park"
                className="w-full h-96 object-cover rounded-lg shadow-lg"
              />
            </div>
          </div>
        </div>
      </section>

      {/* How to Get There */}
      <section className="py-16 bg-safari-light">
        <div className="container mx-auto px-4 max-w-6xl">
          <h2 className="text-2xl font-bold text-safari-green mb-8 text-center">How to Get There</h2>
          <div className="grid md:grid-cols-3 gap-8">
            <div className="card">
              <div className="p-6">
                <h3 className="text-xl font-bold text-safari-green mb-4 flex items-center gap-2">
                  <FontAwesomeIcon icon="fa-plane" className="w-6 h-6" />
                  By Air
                </h3>
                <div className="space-y-3 text-gray-700">
                  <p><strong>Kihihi Airstrip:</strong> Daily scheduled and charter flights from Entebbe (1 hour). Closest airstrip to Buhoma sector (1.5-2 hours drive).</p>
                  <p><strong>Kisoro Airstrip:</strong> Serves southern sectors (Nkuringo, Rushaga). 1-hour flight from Entebbe, then 1.5-2 hours drive to park.</p>
                  <p className="text-sm italic">Flight costs vary by operator and season. Charter flights available for groups.</p>
                </div>
              </div>
            </div>
            <div className="card">
              <div className="p-6">
                <h3 className="text-xl font-bold text-safari-green mb-4 flex items-center gap-2">
                  <FontAwesomeIcon icon="fa-car" className="w-6 h-6" />
                  By Road from Kampala
                </h3>
                <div className="space-y-3 text-gray-700">
                  <p><strong>Via Kabale (8-9 hours):</strong> Most common route through Mbarara and Kabale. Well-maintained tarmac road, scenic journey through terraced hillsides.</p>
                  <p><strong>Distance:</strong> Approximately 460-540 km depending on sector destination.</p>
                  <p className="text-sm italic">4WD vehicle recommended, especially during rainy seasons. Organized transport available through tour operators.</p>
                </div>
              </div>
            </div>
            <div className="card">
              <div className="p-6">
                <h3 className="text-xl font-bold text-safari-green mb-4 flex items-center gap-2">
                  <FontAwesomeIcon icon="fa-globe" className="w-6 h-6" />
                  From Kigali, Rwanda
                </h3>
                <div className="space-y-3 text-gray-700">
                  <p><strong>Via Cyanika Border (4-5 hours):</strong> Popular option for international visitors. Shorter drive than from Kampala, well-maintained roads.</p>
                  <p><strong>Via Katuna Border (5-6 hours):</strong> Alternative route through northern Rwanda.</p>
                  <p className="text-sm italic">Ensure you have East Africa Tourist Visa for cross-border travel. Visas available on arrival.</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Key Highlights */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4 max-w-6xl">
          <h2 className="text-2xl font-bold text-safari-green mb-8 text-center">Key Highlights</h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            <div className="card group">
              <div className="relative h-32 overflow-hidden flex-shrink-0 bg-gradient-to-br from-safari-green to-safari-gold flex items-center justify-center">
                <FontAwesomeIcon icon="fa-paw" className="w-16 h-16 text-white" />
              </div>
              <div className="p-6">
                <h3 className="text-lg font-bold text-safari-green mb-2">Mountain Gorilla Encounters</h3>
                <p className="text-gray-700">
                  25 habituated gorilla families across four sectors provide the world's most accessible mountain gorilla
                  viewing. Each family offers unique personalities and behaviors, with success rates exceeding 95%.
                </p>
              </div>
            </div>
            <div className="card group">
              <div className="relative h-32 overflow-hidden flex-shrink-0 bg-gradient-to-br from-safari-green to-safari-gold flex items-center justify-center">
                <FontAwesomeIcon icon="fa-water" className="w-16 h-16 text-white" />
              </div>
              <div className="p-6">
                <h3 className="text-lg font-bold text-safari-green mb-2">Munyaga Waterfalls</h3>
                <p className="text-gray-700">
                  This spectacular 33-meter cascade provides a perfect complement to gorilla trekking through a
                  manageable 3-hour forest hike from Buhoma sector.
                </p>
              </div>
            </div>
            <div className="card group">
              <div className="relative h-32 overflow-hidden flex-shrink-0 bg-gradient-to-br from-safari-green to-safari-gold flex items-center justify-center">
                <FontAwesomeIcon icon="fa-mountain" className="w-16 h-16 text-white" />
              </div>
              <div className="p-6">
                <h3 className="text-lg font-bold text-safari-green mb-2">Rushura Hill Viewpoint</h3>
                <p className="text-gray-700">
                  360-degree panoramic vistas encompass the Virunga Volcanoes, Rwenzori Mountains, and Western Rift
                  Valley. This moderate 3-hour climb offers spectacular sunrise and sunset viewing.
                </p>
              </div>
            </div>
            <div className="card group">
              <div className="relative h-32 overflow-hidden flex-shrink-0 bg-gradient-to-br from-safari-green to-safari-gold flex items-center justify-center">
                <FontAwesomeIcon icon="fa-dove" className="w-16 h-16 text-white" />
              </div>
              <div className="p-6">
                <h3 className="text-lg font-bold text-safari-green mb-2">Mubwindi Swamp</h3>
                <p className="text-gray-700">
                  A birding paradise with over 200 species including rare Albertine Rift endemics like the African Green
                  Broadbill and Shelley's Crimson-wing.
                </p>
              </div>
            </div>
            <div className="card group">
              <div className="relative h-32 overflow-hidden flex-shrink-0 bg-gradient-to-br from-safari-green to-safari-gold flex items-center justify-center">
                <FontAwesomeIcon icon="fa-people-group" className="w-16 h-16 text-white" />
              </div>
              <div className="p-6">
                <h3 className="text-lg font-bold text-safari-green mb-2">Batwa Heritage Trail</h3>
                <p className="text-gray-700">
                  Cultural immersion experiences with the forest's original inhabitants provide insights into traditional
                  forest survival skills and spiritual connections to the ancient ecosystem.
                </p>
              </div>
            </div>
            <div className="card group">
              <div className="relative h-32 overflow-hidden flex-shrink-0 bg-gradient-to-br from-safari-green to-safari-gold flex items-center justify-center">
                <FontAwesomeIcon icon="fa-tree" className="w-16 h-16 text-white" />
              </div>
              <div className="p-6">
                <h3 className="text-lg font-bold text-safari-green mb-2">Bamboo Forest Ecosystem</h3>
                <p className="text-gray-700">
                  High-altitude bamboo zones above 2,400 meters offer unique trekking experiences through specialized
                  habitats supporting distinct wildlife communities.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Activities & Experiences */}
      <section className="py-16 bg-safari-light">
        <div className="container mx-auto px-4 max-w-6xl">
          <h2 className="text-2xl font-bold text-safari-green mb-8 text-center">Activities & Experiences</h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            <div className="card group">
              <div className="relative h-40 overflow-hidden flex-shrink-0 bg-gradient-to-br from-safari-green to-safari-gold flex items-center justify-center">
                <FontAwesomeIcon icon="fa-binoculars" className="w-20 h-20 text-white" />
              </div>
              <div className="p-6 flex flex-col flex-grow">
                <h3 className="text-lg font-bold text-safari-green mb-2">Gorilla Trekking</h3>
                <p className="text-gray-700 mb-4 flex-grow">
                  Track habituated mountain gorilla families through misty rainforest. Spend one magical hour observing these gentle giants in their natural habitat. Daily departures from all four sectors.
                </p>
                <div className="space-y-2 mb-4 text-sm text-gray-600">
                  <p><FontAwesomeIcon icon="fa-clock" className="w-4 h-4 mr-2" />Duration: 2-8 hours</p>
                  <p><FontAwesomeIcon icon="fa-chart-simple" className="w-4 h-4 mr-2" />Difficulty: Very Strenuous</p>
                </div>
                <p className="text-safari-gold font-bold mb-3 text-center text-lg">From $800 per person</p>
                <Link to="/activities/gorilla-trekking" className="btn-primary w-full text-center block">
                  View Experience
                </Link>
              </div>
            </div>
            <div className="card group">
              <div className="relative h-40 overflow-hidden flex-shrink-0 bg-gradient-to-br from-safari-green to-safari-gold flex items-center justify-center">
                <FontAwesomeIcon icon="fa-clock" className="w-20 h-20 text-white" />
              </div>
              <div className="p-6 flex flex-col flex-grow">
                <h3 className="text-lg font-bold text-safari-green mb-2">Gorilla Habituation Experience</h3>
                <p className="text-gray-700 mb-4 flex-grow">
                  Join researchers for a full day with semi-habituated gorilla groups. Enjoy four hours of close observation while contributing to conservation science. Available in Rushaga sector.
                </p>
                <div className="space-y-2 mb-4 text-sm text-gray-600">
                  <p><FontAwesomeIcon icon="fa-clock" className="w-4 h-4 mr-2" />Duration: 4 hours with gorillas</p>
                  <p><FontAwesomeIcon icon="fa-chart-simple" className="w-4 h-4 mr-2" />Difficulty: Very Strenuous</p>
                </div>
                <p className="text-safari-gold font-bold mb-3 text-center text-lg">From $1,500 per person</p>
                <Link to="/activities/gorilla-habituation" className="btn-primary w-full text-center block">
                  View Experience
                </Link>
              </div>
            </div>
            <div className="card group">
              <div className="relative h-40 overflow-hidden flex-shrink-0 bg-gradient-to-br from-safari-green to-safari-gold flex items-center justify-center">
                <FontAwesomeIcon icon="fa-masks-theater" className="w-20 h-20 text-white" />
              </div>
              <div className="p-6 flex flex-col flex-grow">
                <h3 className="text-lg font-bold text-safari-green mb-2">Batwa Cultural Trail</h3>
                <p className="text-gray-700 mb-4 flex-grow">
                  Walk with Batwa pygmies through the ancient forest. Learn traditional hunting techniques, medicinal plants, and fire-making. Visit sacred caves and hear stories of forest life before resettlement.
                </p>
                <div className="space-y-2 mb-4 text-sm text-gray-600">
                  <p><FontAwesomeIcon icon="fa-clock" className="w-4 h-4 mr-2" />Duration: 3-4 hours</p>
                  <p><FontAwesomeIcon icon="fa-chart-simple" className="w-4 h-4 mr-2" />Difficulty: Moderate</p>
                </div>
                <p className="text-safari-gold font-bold mb-3 text-center text-lg">From $30 per person</p>
                <Link to="/activities/batwa-trail" className="btn-primary w-full text-center block">
                  View Experience
                </Link>
              </div>
            </div>
            <div className="card group">
              <div className="relative h-40 overflow-hidden flex-shrink-0 bg-gradient-to-br from-safari-green to-safari-gold flex items-center justify-center">
                <FontAwesomeIcon icon="fa-feather" className="w-20 h-20 text-white" />
              </div>
              <div className="p-6 flex flex-col flex-grow">
                <h3 className="text-lg font-bold text-safari-green mb-2">Birdwatching Treks</h3>
                <p className="text-gray-700 mb-4 flex-grow">
                  Discover 375 bird species including 23 Albertine Rift endemics. Guided treks through Mubwindi Swamp and forest trails reveal rare species like Shelley's Crimsonwing and African Green Broadbill.
                </p>
                <div className="space-y-2 mb-4 text-sm text-gray-600">
                  <p><FontAwesomeIcon icon="fa-clock" className="w-4 h-4 mr-2" />Duration: 3-5 hours</p>
                  <p><FontAwesomeIcon icon="fa-chart-simple" className="w-4 h-4 mr-2" />Difficulty: Easy to Moderate</p>
                </div>
                <p className="text-safari-gold font-bold mb-3 text-center text-lg">From $40 per person</p>
                <Link to="/activities/bwindi-birdwatching" className="btn-primary w-full text-center block">
                  View Experience
                </Link>
              </div>
            </div>
            <div className="card group">
              <div className="relative h-40 overflow-hidden flex-shrink-0 bg-gradient-to-br from-safari-green to-safari-gold flex items-center justify-center">
                <FontAwesomeIcon icon="fa-person-hiking" className="w-20 h-20 text-white" />
              </div>
              <div className="p-6 flex flex-col flex-grow">
                <h3 className="text-lg font-bold text-safari-green mb-2">Waterfall Hikes</h3>
                <p className="text-gray-700 mb-4 flex-grow">
                  Trek to the spectacular 33-meter Munyaga Waterfall through pristine rainforest. The 3-hour round trip from Buhoma offers excellent photography opportunities and refreshing forest atmosphere.
                </p>
                <div className="space-y-2 mb-4 text-sm text-gray-600">
                  <p><FontAwesomeIcon icon="fa-clock" className="w-4 h-4 mr-2" />Duration: 3 hours</p>
                  <p><FontAwesomeIcon icon="fa-chart-simple" className="w-4 h-4 mr-2" />Difficulty: Moderate</p>
                </div>
                <p className="text-safari-gold font-bold mb-3 text-center text-lg">From $35 per person</p>
                <Link to="/activities/bwindi-nature-walks" className="btn-primary w-full text-center block">
                  View Experience
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Travel Tips */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4 max-w-6xl">
          <h2 className="text-2xl font-bold text-safari-green mb-8 text-center">Travel Tips</h2>
          <div className="grid md:grid-cols-2 gap-8">
            <div className="card">
              <div className="p-6">
                <h3 className="text-xl font-bold text-safari-green mb-4 flex items-center gap-2">
                  <FontAwesomeIcon icon="fa-calendar" className="w-6 h-6" />
                  Best Time to Visit
                </h3>
                <div className="space-y-3 text-gray-700">
                  <p><strong>Dry Seasons (June-September, December-February):</strong> Easier trekking conditions with less muddy trails and clearer views. Peak tourist season with higher demand for permits.</p>
                  <p><strong>Wet Seasons (March-May, October-November):</strong> Challenging but rewarding trekking through lush forest. Fewer visitors and excellent photographic opportunities with dramatic cloud formations.</p>
                  <p className="text-sm italic">Gorilla trekking is a year-round activity. Book permits 3-6 months in advance regardless of season.</p>
                </div>
              </div>
            </div>
            <div className="card">
              <div className="p-6">
                <h3 className="text-xl font-bold text-safari-green mb-4 flex items-center gap-2">
                  <FontAwesomeIcon icon="fa-suitcase" className="w-6 h-6" />
                  What to Pack
                </h3>
                <div className="space-y-2 text-gray-700">
                  <p><strong>Essential:</strong> Waterproof hiking boots, rain jacket, long pants and sleeves, gardening gloves for grabbing vegetation</p>
                  <p><strong>Recommended:</strong> Daypack, insect repellent, sunscreen, hat, reusable water bottle</p>
                  <p><strong>Camera Gear:</strong> No flash allowed. Bring extra batteries as charging may be limited</p>
                  <p className="text-sm italic">Porters available to carry daypacks and assist on steep sections. Consider hiring to support local communities.</p>
                </div>
              </div>
            </div>
            <div className="card">
              <div className="p-6">
                <h3 className="text-xl font-bold text-safari-green mb-4 flex items-center gap-2">
                  <FontAwesomeIcon icon="fa-heartbeat" className="w-6 h-6" />
                  Health & Safety
                </h3>
                <div className="space-y-2 text-gray-700">
                  <p><strong>Fitness Level:</strong> Moderate to high fitness required. Treks can last 2-8 hours over steep, muddy terrain at altitude.</p>
                  <p><strong>Vaccinations:</strong> Yellow fever certificate required. Malaria prophylaxis recommended.</p>
                  <p><strong>Age Limit:</strong> Minimum age 15 years for gorilla trekking.</p>
                  <p><strong>Health Screening:</strong> Anyone with communicable illness will not be permitted to trek (protects gorillas).</p>
                </div>
              </div>
            </div>
            <div className="card">
              <div className="p-6">
                <h3 className="text-xl font-bold text-safari-green mb-4 flex items-center gap-2">
                  <FontAwesomeIcon icon="fa-coins" className="w-6 h-6" />
                  Budgeting Tips
                </h3>
                <div className="space-y-2 text-gray-700">
                  <p><strong>Permits:</strong> Book well in advance through Uganda Wildlife Authority or authorized tour operators.</p>
                  <p><strong>Accommodation:</strong> Range from budget hostels to luxury lodges. Book sector accommodation based on your trekking permit sector.</p>
                  <p><strong>Tipping:</strong> Budget for guide, tracker, and porter tips (typically $10-20 per person).</p>
                  <p><strong>Extras:</strong> Community visits, additional activities, and craft purchases support local economies.</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Where to Stay */}
      <section className="py-16 bg-safari-light">
        <div className="container mx-auto px-4 max-w-6xl">
          <h2 className="text-2xl font-bold text-safari-green mb-6 text-center">Where to Stay</h2>
          <p className="text-center text-gray-700 mb-12 max-w-3xl mx-auto">
            Accommodation options are available near each of Bwindi's four sectors. Choose your lodging based on which sector your gorilla permit is allocated to.
          </p>
          <div className="overflow-x-auto">
            <table className="w-full bg-white rounded-lg shadow-lg">
              <thead className="bg-safari-green text-white">
                <tr>
                  <th className="px-6 py-4 text-left">Category</th>
                  <th className="px-6 py-4 text-left">Sector</th>
                  <th className="px-6 py-4 text-left">Accommodation Options</th>
                  <th className="px-6 py-4 text-left">Features</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                <tr className="hover:bg-gray-50">
                  <td className="px-6 py-4 font-bold text-safari-green">Luxury</td>
                  <td className="px-6 py-4">Buhoma</td>
                  <td className="px-6 py-4">Buhoma Lodge, Sanctuary Gorilla Forest Camp, Mahogany Springs</td>
                  <td className="px-6 py-4">En-suite rooms, restaurant, spa, forest views</td>
                </tr>
                <tr className="hover:bg-gray-50">
                  <td className="px-6 py-4 font-bold text-safari-green">Luxury</td>
                  <td className="px-6 py-4">Nkuringo</td>
                  <td className="px-6 py-4">Clouds Mountain Gorilla Lodge, Nkuringo Bwindi Gorilla Lodge</td>
                  <td className="px-6 py-4">Panoramic views, fine dining, intimate setting</td>
                </tr>
                <tr className="hover:bg-gray-50">
                  <td className="px-6 py-4 font-bold text-safari-green">Mid-Range</td>
                  <td className="px-6 py-4">Buhoma</td>
                  <td className="px-6 py-4">Silverback Lodge, Engagi Lodge, Haven Lodge</td>
                  <td className="px-6 py-4">Comfortable cottages, good food, forest setting</td>
                </tr>
                <tr className="hover:bg-gray-50">
                  <td className="px-6 py-4 font-bold text-safari-green">Mid-Range</td>
                  <td className="px-6 py-4">Rushaga</td>
                  <td className="px-6 py-4">Gorilla Valley Lodge, Ichumbi Gorilla Lodge</td>
                  <td className="px-6 py-4">Mountain views, hearty meals, close to park</td>
                </tr>
                <tr className="hover:bg-gray-50">
                  <td className="px-6 py-4 font-bold text-safari-green">Budget</td>
                  <td className="px-6 py-4">Buhoma</td>
                  <td className="px-6 py-4">Buhoma Community Rest Camp, Ride 4 a Woman</td>
                  <td className="px-6 py-4">Basic rooms, community-owned, affordable</td>
                </tr>
                <tr className="hover:bg-gray-50">
                  <td className="px-6 py-4 font-bold text-safari-green">Budget</td>
                  <td className="px-6 py-4">Ruhija</td>
                  <td className="px-6 py-4">Ruhija Gorilla Friends Resort, Trekkers Tavern</td>
                  <td className="px-6 py-4">Simple bandas, excellent birding location</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {/* FAQs */}
      <FAQSection faqs={faqs} />

      {/* Call to Action */}
      <section className="py-16 bg-safari-green text-white">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-6">
            Ready to Meet the Mountain Gorillas?
          </h2>
          <p className="text-xl mb-8 max-w-2xl mx-auto">
            Embark on a life-changing journey into Bwindi's ancient rainforest. Our expert guides and carefully
            planned itineraries ensure you experience the magic of gorilla trekking while supporting vital conservation efforts.
          </p>
          <button
            onClick={() => handleBooking('Complete Bwindi Experience')}
            className="btn-primary text-lg"
          >
            Plan Your Bwindi Adventure
          </button>
        </div>
      </section>

      <BookingModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        experience={selectedExperience}
      />
    </div>
  )
}
